import { useCallback, useRef, useState } from 'react';

/**
 * Shape of a single ~1s WebRTC sample, as the panel consumes it.
 *
 * The field names and units match one provider's SDK `RTCSample` interface,
 * which this deliberately mirrors without importing, because that provider's
 * adapter can pass its samples straight through. The other adapter builds the
 * same shape from the native `RTCPeerConnection.getStats()` report, so
 * anything added here has to be answerable from both.
 */
export interface CallQualitySample {
  mos: number | null;
  jitter: number;
  rtt: number;
  packetsLost: number;
  /** Percentage of packets lost, 0-100 — not a 0-1 fraction, despite the name. */
  packetsLostFraction: number;
  packetsSent: number;
  packetsReceived: number;
  bytesSent: number;
  bytesReceived: number;
  audioInputLevel: number;
  audioOutputLevel: number;
  codecName: string;
  timestamp: number;
}

/** Rolling window of MOS scores for sparkline rendering. */
const MAX_MOS_HISTORY = 60; // ~60 seconds of data

export type MosQuality = 'excellent' | 'good' | 'fair' | 'poor' | 'bad' | 'unknown';

export interface CallQualityState {
  /** Most recent sample from the SDK. */
  current: CallQualitySample | null;
  /** Rolling window of MOS values (oldest first). */
  mosHistory: (number | null)[];
  /** Active SDK warnings (e.g. "high-jitter", "low-mos"). */
  activeWarnings: Set<string>;
}

/** Classify a MOS score into a named quality tier. */
export function getMosQuality(mos: number | null): MosQuality {
  if (mos === null || mos === undefined) return 'unknown';
  if (mos >= 4.0) return 'excellent';
  if (mos >= 3.5) return 'good';
  if (mos >= 3.0) return 'fair';
  if (mos >= 2.5) return 'poor';
  return 'bad';
}

/** Map quality tier to a theme-safe Tailwind color class (text). */
export function getMosColor(quality: MosQuality): string {
  switch (quality) {
    case 'excellent':
      return 'text-positive-foreground';
    case 'good':
      return 'text-primary';
    case 'fair':
      return 'text-attention-foreground';
    case 'poor':
      return 'text-orange-600 dark:text-orange-400';
    case 'bad':
      return 'text-destructive';
    default:
      return 'text-muted-foreground';
  }
}

/** Map quality tier to a background color class for the badge. */
export function getMosBgColor(quality: MosQuality): string {
  switch (quality) {
    case 'excellent':
      return 'bg-positive-muted';
    case 'good':
      return 'bg-primary/10';
    case 'fair':
      return 'bg-attention-muted';
    case 'poor':
      return 'bg-orange-100 dark:bg-orange-950/30';
    case 'bad':
      return 'bg-destructive/10';
    default:
      return 'bg-muted';
  }
}

/**
 * Manages call quality sample state for the dialpad.
 *
 * Returns handlers that should be wired to Twilio SDK events:
 * - `onSample` → `call.on('sample', onSample)`
 * - `onWarning` → `call.on('warning', onWarning)`
 * - `onWarningCleared` → `call.on('warning-cleared', onWarningCleared)`
 * - `reset` → call on disconnect / cleanup
 */
export function useCallQuality() {
  const [state, setState] = useState<CallQualityState>({
    current: null,
    mosHistory: [],
    activeWarnings: new Set(),
  });

  // Use a ref for the history buffer to avoid re-render on every sample
  // — we batch-flush to state at a lower cadence via the sample handler.
  const historyRef = useRef<(number | null)[]>([]);

  const onSample = useCallback((sample: CallQualitySample) => {
    // Append MOS to rolling history
    historyRef.current.push(sample.mos);
    if (historyRef.current.length > MAX_MOS_HISTORY) {
      historyRef.current.shift();
    }

    setState(prev => ({
      ...prev,
      current: sample,
      mosHistory: [...historyRef.current],
    }));
  }, []);

  const onWarning = useCallback((name: string) => {
    setState(prev => {
      const next = new Set(prev.activeWarnings);
      next.add(name);
      return { ...prev, activeWarnings: next };
    });
  }, []);

  const onWarningCleared = useCallback((name: string) => {
    setState(prev => {
      const next = new Set(prev.activeWarnings);
      next.delete(name);
      return { ...prev, activeWarnings: next };
    });
  }, []);

  const reset = useCallback(() => {
    historyRef.current = [];
    setState({
      current: null,
      mosHistory: [],
      activeWarnings: new Set(),
    });
  }, []);

  return {
    callQuality: state,
    onSample,
    onWarning,
    onWarningCleared,
    resetCallQuality: reset,
  };
}
